import type { ProductData } from "./types/Product";
import { Catalog } from "./types/Catalog";
import { SceneResponse } from "./types/Scene";
import {
  API_BASE_URL,
  CONFIGURATOR_API_URL,
  CONFIGURATOR_CDN_URL,
  DYNAMIC_TAG_URL,
  DYNAMIC_TAG_URL_OLD,
  PLANNER_URL,
  PRODUCT_MODEL_URL,
} from "../config/api";

export {
  API_BASE_URL,
  CONFIGURATOR_API_URL,
  CONFIGURATOR_CDN_URL,
  DYNAMIC_TAG_URL as API_URL,
  DYNAMIC_TAG_URL_OLD as API_URL_OLD,
};

const AUTH = "Basic " + btoa("planner.default@sugartech.io:planner");

type FetchProgressOptions = {
  onProgress?: (progress: number) => void;
};

async function responseJsonWithProgress(
  response: Response,
  options?: FetchProgressOptions,
) {
  const contentLength = Number(response.headers.get("Content-Length") ?? 0);
  if (!response.body || !Number.isFinite(contentLength) || contentLength <= 0) {
    const data = await response.json();
    options?.onProgress?.(1);
    return data;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    loaded += value.length;
    options?.onProgress?.(Math.min(1, loaded / contentLength));
  }

  const bytes = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }

  options?.onProgress?.(1);
  return JSON.parse(new TextDecoder().decode(bytes));
}

export class Api {
  static async fetchCatalog(identifier: string): Promise<Catalog> {
    const response = await fetch(
      CONFIGURATOR_API_URL + "/configurator/app/zip/" + identifier,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const catalog: Catalog = await response.json();
    return Api.sortCatalog(catalog);
  }

  private static sortCatalog(catalog: Catalog): Catalog {
    if (catalog.categories?.length) {
      catalog.categories = catalog.categories
        .sort(
          (a, b) => (a.catelogIndex ?? 0) - (b.catelogIndex ?? 0),
        )
        .map((category) => {
          if (category.products?.length) {
            category.products = category.products.sort(
              (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
            );
          }
          return category;
        });
    }
    return catalog;
  }

  static async fetchConfiguratorProduct(productId: string): Promise<any> {
    let data = await fetch(
      CONFIGURATOR_API_URL + "/configurator/product/" + productId,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return await (data as any).json();
  }

  static async fetchProductOld(
    customerId: string | undefined,
    companyId: number,
    options?: FetchProgressOptions,
  ): Promise<ProductData> {
    options?.onProgress?.(0.05);
    let data = await fetch(
      DYNAMIC_TAG_URL_OLD +
        "?" +
        "productId=" +
        customerId +
        "&" +
        "companyId=" +
        companyId +
        "&" +
        "withFeature=false",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return await responseJsonWithProgress(data, options);
  }

  static async fetchProduct(
    customerId: string | undefined,
    productId: number | undefined,
    companyId?: number,
    options?: FetchProgressOptions,
  ): Promise<ProductData> {
    options?.onProgress?.(0.05);
    const params = new URLSearchParams();
    if (productId) params.set("sugarProductId", String(productId));
    else if (customerId) params.set("productId", String(customerId));
    if (companyId != null && Number.isFinite(companyId)) {
      params.set("companyId", String(companyId));
    }
    params.set("withFeature", "false");
    let data = await fetch(`${DYNAMIC_TAG_URL}?${params.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return await responseJsonWithProgress(data, options);
  }

  static async fetchHDRS(appId: string) {
    let data = await fetch(`${PLANNER_URL}/get-hdrs`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return await (data as any).json();
  }

  static async fetchCustomerProduct(customerId: number, companyId: number) {
    let data = await fetch(
      DYNAMIC_TAG_URL +
        "?" +
        "productId=" +
        customerId +
        "&" +
        "companyId=" +
        companyId,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return await (data as any).json();
  }

  static async uploadScene(scene: any) {
    let data = await fetch(PLANNER_URL + "/save-scene", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: AUTH,
      },
      body: JSON.stringify(scene),
    });

    return await (data as any).json();
  }

  static async fetchScene(inAppId: number) {
    let response = await fetch(PLANNER_URL + "/get-scene?inAppId=" + inAppId, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: AUTH,
      },
    });

    const data: SceneResponse = await (response as any).json();
    data.scene = await fetch(data.fileUrl).then((res) => res.json());
    return data;
  }

  static async fetchAllScenes() {
    let data = await fetch(PLANNER_URL + "/list-scene", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: AUTH,
      },
    });

    return await (data as any).json();
  }

  static async fetchConfigurator(id: string) {
    const response = await fetch(CONFIGURATOR_API_URL + "/configurator/zip/" + id, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return await response.json();
  }

  static async getProductsOfScene(inAppId: number) {
    return Api.fetchScene(inAppId).then((data) => {
      const scene = data.scene;
      return {
        products: scene?.products,
        productInstances: scene?.productInstances,
      };
    });
  }

  static async getTools(inAppId: number | string | undefined) {
    if (inAppId == "sbalu") {
      return [
        {
          name: "drawer",
          state: "drawer",
          type: "balcony",
          icon: `<svg class="container-svg" data-v-a281fab8-s="">
            <use xlink:href="#glass-wall"></use>
          </svg>`,
        },
      ];
    }
    return [
      {
        name: "drawer",
        state: "drawer",
        type: "wall",
        icon: `<svg class="container-svg" data-v-a281fab8-s="">
          <use xlink:href="#brick-wall"></use>
        </svg>`,
      },
      {
        name: "window",
        state: "window",
        icon: ` <svg class="container-svg" data-v-a281fab8-s="">
          <use xlink:href="#icon-room"></use>
        </svg>`,
      },
      {
        name: "door",
        state: "door",
        icon: ` <svg class="container-svg" data-v-a281fab8-s="">
          <use xlink:href="#glass-wall"></use>
        </svg>`,
      },
    ];
  }

  static async getNavBarConfig(inAppId: number | string | undefined) {
    if (inAppId == "sbalu") {
      return {
        hasNavigation: true,
        panes: [
          {
            title: "drawWall",
            name: "shape-room",
            index: 0,
          },
          {
            title: "addToRoom",
            name: "sbaluproduct",
            index: 1,
          },
          {
            title: "configure",
            name: "wall",
            index: 2,
          },
          {
            title: "lighting",
            name: "lighting",
            index: 3,
          },
          {
            title: "export",
            name: "export",
            type: "execute",
            index: 4,
          },
        ],
      };
    }
    return {
      hasNavigation: false,
      panes: [
        {
          title: "addToRoom",
          name: "product",
        },

        {
          title: "roomFeatures",
          name: "feat",
        },

        {
          title: "roomStyle",
          name: "surfaces",
        },
        {
          title: "structural.menu",
          name: "structure",
        },
        {
          title: "lighting",
          name: "lighting",
        },

        {
          title: "drawWall",
          name: "shape-room",
        },
      ],
    };
  }

  static async saveJoint(body: any) {
    let data = await fetch(PRODUCT_MODEL_URL + "/save/joint", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await (data as any).json();
  }

  static async deleteJoint(body: any) {
    let data = await fetch(PRODUCT_MODEL_URL + "/delete/joint", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return await (data as any).text();
  }
}
